import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Plus, PushPin } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";
import { Button } from "./ui/button";

// Reusable notes list with add + pin-to-top. `basePath` is the entity path, e.g.
// `/customers/<id>` or `/vendors/<id>`. Pinned notes sort to the top.
export function NotesPane({ basePath, isNew, notes = [], onChanged, testidPrefix = "entity", readOnly = false }) {
  const [text, setText] = useState("");
  const add = useMutation({
    mutationFn: async () => (await api.post(`${basePath}/notes`, { text })).data,
    onSuccess: () => { setText(""); onChanged(); toast.success("Note added"); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });
  const pin = useMutation({
    mutationFn: async (noteId) => (await api.post(`${basePath}/notes/${noteId}/pin`)).data,
    onSuccess: () => onChanged(),
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });
  if (isNew) return <p className="text-sm text-muted-foreground">Save to add notes.</p>;
  const sorted = [...notes].sort((a, b) => (b.pinned ? 1 : 0) - (a.pinned ? 1 : 0));
  return (
    <div className="space-y-3">
      <div className="space-y-2 max-h-64 overflow-y-auto" data-testid={`${testidPrefix}-notes-list`}>
        {sorted.length === 0 && <p className="text-sm text-muted-foreground">No notes yet.</p>}
        {sorted.map((n) => (
          <div key={n.id} className={`border rounded-sm p-2.5 text-sm ${n.pinned ? "border-primary/40 bg-primary/5" : "border-border"}`} data-testid={`${testidPrefix}-note`}>
            <div className="flex items-start justify-between gap-2">
              <p className="whitespace-pre-wrap flex-1">{n.text}</p>
              {(!readOnly || n.pinned) && <button onClick={() => pin.mutate(n.id)} disabled={pin.isPending || readOnly} title={n.pinned ? "Unpin" : "Pin to top"} data-testid={`${testidPrefix}-note-pin`} className={`shrink-0 p-0.5 transition-colors ${n.pinned ? "text-primary" : "text-muted-foreground hover:text-primary"}`}>
                <PushPin size={15} weight={n.pinned ? "fill" : "regular"} />
              </button>}
            </div>
            <p className="text-[11px] text-muted-foreground mt-1">{n.created_by} · {(n.created_at || "").slice(0, 10)}</p>
          </div>
        ))}
      </div>
      {!readOnly && <textarea value={text} onChange={(e) => setText(e.target.value)} rows={3} placeholder="Add a note…" data-testid={`${testidPrefix}-note-input`} className="w-full rounded-sm border border-border p-2 text-sm outline-none focus:border-primary resize-none" />}
      {!readOnly && <Button onClick={() => add.mutate()} disabled={!text.trim() || add.isPending} data-testid={`${testidPrefix}-add-note`} className="rounded-sm bg-primary hover:bg-primary/90 h-8 text-xs w-full"><Plus size={14} className="mr-1" /> Add Note</Button>}
    </div>
  );
}
